import React, { useState, useRef, useEffect } from 'react';
import { Camera, MapPin, CheckCircle, AlertCircle, Clock, Unlock, Loader2, User, ChevronDown } from 'lucide-react';

const IdentityVerification = ({ onLogout }) => {
  const tempOverride = true; 
  
  // List of Staff Names
  const STAFF_NAMES = ["Shashi Kumar K", "M Pavan Kumar", "Shivaraj N", "Hanish S.S"];

  const [selectedName, setSelectedName] = useState(""); // State for dropdown
  const [isWindowOpen, setIsWindowOpen] = useState(true); 
  const [timeLeft, setTimeLeft] = useState("MANUALLY UNLOCKED");
  const [image, setImage] = useState(null);
  const [location, setLocation] = useState({ lat: null, lng: null, loading: true });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const checkTimeWindow = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const validHours = [11, 12, 13, 14, 15, 16, 17];
    const isCurrentlyInWindow = validHours.includes(hours) && minutes >= 0 && minutes <= 15;

    if (tempOverride) {
      setIsWindowOpen(true);
      setTimeLeft("ADMIN OVERRIDE: OPEN");
    } else {
      setIsWindowOpen(isCurrentlyInWindow);
      if (isCurrentlyInWindow) {
        const minsRemaining = 14 - minutes;
        const secsRemaining = 59 - now.getSeconds();
        setTimeLeft(`${minsRemaining}m ${secsRemaining}s`);
      }
    }
  };

  useEffect(() => {
    checkTimeWindow();
    const timer = setInterval(checkTimeWindow, 1000);
    getLocation();
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isWindowOpen && !image) startCamera();
  }, [isWindowOpen, image]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user" }, 
        audio: false 
      });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      setError("Camera blocked. Please allow access.");
    }
  };

  const getLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6), loading: false }),
      () => { 
        setError("GPS Required."); 
        setLocation({ lat: "DENIED", lng: "DENIED", loading: false }); 
      },
      { enableHighAccuracy: true }
    );
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    setImage(canvas.toDataURL('image/png'));
  };

  const handleFinalSubmit = async () => {
    if (!selectedName) {
        setError("Please select your name from the dropdown.");
        return;
    }
    if (!image || location.loading || location.lat === "DENIED") {
      setError("Incomplete data. Ensure photo and GPS are ready.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    const payload = {
      userName: selectedName, // Using the selected name from dropdown
      image: image,
      location: { lat: location.lat, lng: location.lng }
    };

    try {
      const response = await fetch("https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/identity/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert("✅ Identity Verified & Saved!");
        setImage(null);
      } else {
        const data = await response.json();
        setError(data.message || "Failed to save verification.");
      }
    } catch (err) {
      setError("Server unreachable. Check connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isWindowOpen) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl">
          <Clock className="mx-auto text-orange-500 mb-4" size={48} />
          <h2 className="text-2xl font-bold">Verification Closed</h2>
          <button onClick={onLogout} className="mt-6 text-indigo-600 font-semibold underline">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-4 font-sans">
      
      <div className="w-full max-w-md mb-4 flex justify-between items-center bg-white p-3 rounded-xl shadow-sm border border-purple-100">
        <div className="flex items-center gap-2">
          <Unlock size={14} className="text-purple-600 animate-pulse" />
          <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest">Unlocked Mode</span>
        </div>
        <span className="text-[10px] font-mono bg-purple-50 text-purple-700 px-2 py-1 rounded-md font-bold">{timeLeft}</span>
      </div>

      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100">
        <div className="bg-slate-900 p-6 text-white text-center">
          <h1 className="text-xl font-bold tracking-tight uppercase">Identity Capture</h1>
          <p className="text-slate-400 text-[10px] mt-1 tracking-[0.2em] font-medium italic">Verification Portal</p>
        </div>

        <div className="p-6 space-y-5">
          {/* --- NEW NAME DROPDOWN --- */}
          <div className="relative">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2 ml-1">Select Staff Member</label>
            <div className="relative group">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                <User size={18} />
              </span>
              <select
                required
                value={selectedName}
                onChange={(e) => { setSelectedName(e.target.value); setError(""); }}
                className="w-full py-3 pl-11 pr-10 bg-slate-50 border border-slate-200 text-gray-700 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer transition-all"
              >
                <option value="" disabled>Choose your name</option>
                {STAFF_NAMES.map((name, i) => (
                  <option key={i} value={name}>{name}</option>
                ))}
              </select>
              <span className="absolute inset-y-0 right-0 flex items-center pr-3.5 pointer-events-none text-gray-400">
                <ChevronDown size={18} />
              </span>
            </div>
          </div>

          <div className="relative aspect-square bg-slate-900 rounded-2xl overflow-hidden shadow-inner ring-4 ring-slate-50">
            {!image ? (
              <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
            ) : (
              <img src={image} alt="Identity" className="w-full h-full object-cover animate-in fade-in duration-300" />
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div className="flex justify-between font-mono text-xs text-slate-800 font-bold">
              <span>LAT: {location.loading ? '...' : location.lat}</span>
              <span>LNG: {location.loading ? '...' : location.lng}</span>
            </div>
          </div>

          {error && (
             <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-xs font-bold">
                <AlertCircle size={14} /> {error}
             </div>
          )}

          <div className="flex gap-3 pt-2">
            {!image ? (
              <button 
                onClick={capturePhoto}
                className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <Camera size={20} /> Capture Photo
              </button>
            ) : (
              <>
                <button 
                  onClick={() => setImage(null)} 
                  disabled={isSubmitting}
                  className="px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 disabled:opacity-50"
                >
                  Retake
                </button>
                <button 
                  onClick={handleFinalSubmit}
                  disabled={isSubmitting}
                  className="flex-1 py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 flex items-center justify-center gap-2 shadow-lg shadow-emerald-100 transition-all active:scale-95 disabled:opacity-70"
                >
                  {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle size={20} />}
                  {isSubmitting ? "Uploading..." : "Submit Identity"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <button onClick={onLogout} className="mt-8 text-slate-400 text-[10px] font-bold uppercase hover:text-red-500 transition-colors">End Session</button>
    </div>
  );
};

export default IdentityVerification;